import {
  tryParseStaleReason,
  type ArtifactEventPayload,
  type ArtifactStatus,
  type DomainErrorCode,
  DRAFT_ARTIFACT_CHAIN,
  type DraftArtifactKind,
  type StaleReason,
  type StudyState,
  staleApprovalText,
} from '@sigora/domain';

import type { ArtifactStateRow } from '../api/client.ts';

/** Plain language, and no vendor or research-industry words anywhere in it. */
export const KIND_LABELS: Record<DraftArtifactKind, string> = {
  rqs: 'Research questions',
  dg: 'Discussion guide',
  audience: 'Audience',
};

export const STATUS_LABELS: Record<ArtifactStatus, string> = {
  unapproved: 'Not approved yet',
  approved: 'Approved',
  stale: 'Needs attention',
};

export interface SidebarArtifact {
  kind: DraftArtifactKind;
  /** Null until the agent has written a first version, which is also "nothing to approve yet". */
  artifactId: string | null;
  version: number;
  approvedVersion: number | null;
  status: ArtifactStatus;
  staleReason: StaleReason | null;
  /** Still arriving, so the chrome holds off on offering an approval for a moving target. */
  streaming: boolean;
  /** Present when the streamed part carried it; otherwise the card fetches on open. */
  content?: unknown;
}

const fromStored = (stored: ArtifactStateRow): SidebarArtifact => ({
  kind: stored.kind as DraftArtifactKind,
  artifactId: stored.artifactId,
  version: stored.currentVersion,
  approvedVersion: stored.approvedVersion,
  status: stored.status,
  staleReason: stored.staleReason,
  streaming: false,
});

const fromStreamed = (streamed: ArtifactEventPayload): SidebarArtifact => ({
  kind: streamed.kind as DraftArtifactKind,
  artifactId: streamed.artifact_id,
  version: streamed.version,
  approvedVersion: null,
  status: streamed.status,
  // Lifted here rather than parsed in the event schema: a strict parse there returns null for the
  // whole frame, so an older payload would make the event vanish instead of rendering. Non-throwing,
  // because this runs during render and there is no boundary above the sidebar — one unreadable field
  // must cost that artifact its flag, not cost the user every artifact on screen.
  staleReason: tryParseStaleReason(streamed.stale_reason),
  streaming: streamed.streaming,
  content: streamed.content,
});

/**
 * Reconciles what the server says an artifact is with what the current turn has been streaming
 * about it.
 *
 * The stored row is the authority on chrome — approval pointers and staleness are settled by
 * writes this stream knows nothing about, an approval among them — while the streamed part is
 * the only place the freshly written document exists before anything refetches. So the version
 * numbers decide: a streamed part behind the stored version is a leftover from earlier in the
 * conversation and is ignored outright, one level with it contributes its content and nothing
 * else, and one ahead of it means the write landed and the refetch has not caught up yet.
 *
 * Getting this backwards is the visible bug: a part left in the transcript from three turns ago
 * would quietly replace a document the user has edited since.
 */
export const mergeArtifact = ({
  stored,
  streamed,
}: {
  stored?: ArtifactStateRow;
  streamed?: ArtifactEventPayload;
}): SidebarArtifact | null => {
  if (stored === undefined) {
    return streamed === undefined ? null : fromStreamed(streamed);
  }

  if (streamed === undefined || streamed.version < stored.currentVersion) {
    return fromStored(stored);
  }

  if (streamed.version > stored.currentVersion) {
    return fromStreamed(streamed);
  }

  return { ...fromStored(stored), content: streamed.content, streaming: streamed.streaming };
};

/**
 * The sidebar, in approval order. Every chain kind gets an entry whether or not it exists yet,
 * because "the guide has not been written" is a state the user should see rather than an absence
 * they have to infer from a shorter list.
 */
export const buildSidebar = ({
  stored,
  streamed,
}: {
  stored: readonly ArtifactStateRow[];
  streamed: readonly ArtifactEventPayload[];
}): { kind: DraftArtifactKind; artifact: SidebarArtifact | null }[] => {
  const storedByKind = new Map(stored.map((row) => [row.kind, row]));
  // Later events win: the parts arrive in stream order and the last one is the current state.
  const streamedByKind = new Map(streamed.map((event) => [event.kind, event]));

  return DRAFT_ARTIFACT_CHAIN.map((kind) => ({
    kind,
    artifact: mergeArtifact({
      stored: storedByKind.get(kind),
      streamed: streamedByKind.get(kind),
    }),
  }));
};

export type ApprovalState =
  | { can: true }
  | {
      can: false;
      reason: 'not-drafted' | 'streaming' | 'stale' | 'upstream' | 'approved' | 'locked';
      message: string;
    };

/**
 * Whether this artifact's approve button does anything, and if not, why.
 *
 * The order is the product's — research questions, then guide, then audience — and it is enforced
 * here as well as by the agent's own sequencing, because approval means "you may build the next
 * stage on this" and an audience approved before the guide it was drawn from is a claim nobody
 * made. The server refuses a stale approval too; this is what stops the user finding that out by
 * clicking.
 */
export const approvalState = ({
  entries,
  kind,
  locked,
}: {
  entries: readonly { kind: DraftArtifactKind; artifact: SidebarArtifact | null }[];
  kind: DraftArtifactKind;
  locked: boolean;
}): ApprovalState => {
  const byKind = new Map(entries.map((entry) => [entry.kind, entry.artifact]));
  const artifact = byKind.get(kind) ?? null;

  if (locked) {
    // Payment is what the sentence names, because payment is what closes a study. The charge lands
    // minutes before anything fields, so this reason is read most often by a researcher watching
    // their own study still being set up — and nothing is running yet at that moment. What is true
    // of both that study and a fielding one is that it has been paid for. Naming the payment is
    // safe for a locked-but-unpaid study because there is no such study in the product: the only
    // two callers of `lock` are the provisioning run, which a charge starts, and `freeze`, which is
    // reachable only from a route registered when the environment is `local`.
    return {
      can: false,
      reason: 'locked',
      message: 'This study is paid for and can no longer change.',
    };
  }

  if (artifact === null) {
    return { can: false, reason: 'not-drafted', message: 'Not written yet.' };
  }

  if (artifact.streaming) {
    return { can: false, reason: 'streaming', message: 'Still arriving…' };
  }

  if (artifact.status === 'stale') {
    return {
      can: false,
      reason: 'stale',
      message: staleApprovalText(kind, artifact.staleReason),
    };
  }

  for (const upstream of DRAFT_ARTIFACT_CHAIN.slice(0, DRAFT_ARTIFACT_CHAIN.indexOf(kind))) {
    const above = byKind.get(upstream) ?? null;
    if (above === null || above.status !== 'approved') {
      return {
        can: false,
        reason: 'upstream',
        message: `Approve ${KIND_LABELS[upstream].toLowerCase()} first.`,
      };
    }
  }

  if (artifact.status === 'approved') {
    return { can: false, reason: 'approved', message: 'Approved.' };
  }

  return { can: true };
};

/**
 * What a save actually did. `deduped` on its own is not renderable — it covers two different
 * pieces of news — so this is what the UI branches on instead.
 */
export type SaveOutcome =
  | { kind: 'saved'; version: number; message: string }
  | { kind: 'unchanged'; message: string }
  | { kind: 'unchanged-still-stale'; message: string }
  | { kind: 'reconciled'; message: string };

export const describeSave = (result: {
  version: number;
  status: ArtifactStatus;
  deduped: boolean;
  resolvedStaleness: boolean;
}): SaveOutcome => {
  if (!result.deduped) {
    return {
      kind: 'saved',
      version: result.version,
      message: `Saved as version ${String(result.version)}.`,
    };
  }

  if (result.resolvedStaleness) {
    // The agent regenerated this against the change above it and produced the same document, so
    // the change is answered even though no version was added. Nothing is pending any more.
    return {
      kind: 'reconciled',
      message:
        'Checked against the change above it — nothing needed to move, and it is up to date.',
    };
  }

  if (result.status === 'stale') {
    // The sharp one. Saving the same words again is not the same as having looked at what
    // changed upstream, so the flag stays up and the message must not imply otherwise.
    return {
      kind: 'unchanged-still-stale',
      message: 'No changes to save — and this is still waiting on a change above it.',
    };
  }

  return { kind: 'unchanged', message: 'No changes to save.' };
};

/**
 * The words for a refusal, chosen by code. Every one of these is a thing the user can act on,
 * which is the point of not collapsing them into "could not save".
 */
export const refusalMessage = (code: DomainErrorCode | null, fallback: string): string => {
  switch (code) {
    case 'artifact_version_not_current':
      // Never a silent overwrite and never an automatic retry: both would throw away whichever
      // of the two versions lost, and the user is the only one who can say which that should be.
      return 'Someone or something changed this while you were editing. Your text has not been saved — reopen it to see the current version.';
    case 'user_edit_not_allowed':
      return 'Ask the agent for structural changes.';
    // Which research questions a guide question serves is structure, so the repair is the agent's
    // either way: the user's paths refuse a change to `serves_rq_ids` as readily as they refuse a
    // new research question. Saying so beats echoing a message that names two things they cannot do.
    case 'dg_serves_unknown_rq':
      return 'A question in the guide points at a research question the study no longer has. Ask the agent to fix what it serves.';
    case 'dg_question_not_found':
      return 'That question is no longer in the guide. Reopen it to see the current one.';
    case 'artifact_stale':
      return staleApprovalText(null, null);
    case 'study_locked':
      return 'This study is running, so it can no longer be changed.';
    case 'invalid_content':
      return 'That does not look right yet — check the wording and try again.';
    default:
      return fallback;
  }
};

/**
 * Ready is derived, never announced. Approving the last artifact promotes the study inside the
 * same transaction, and an edit that unapproves or stales one demotes it, so the UI reads the
 * state rather than asking for it — there is no transition for it to call.
 */
export const readinessLabel = (state: StudyState): string | null =>
  state === 'ready' ? 'Everything is approved. This study is ready.' : null;

/** Every state a study reaches by being launched, from fielding through to whatever ended it. */
const LAUNCHED_STATES = new Set<StudyState>([
  'live',
  'analyzing',
  'complete',
  'under_recruited',
  'cancelled',
]);

/** Whether a study has launched: fielding, or any state fielding leads to. */
export const isLaunchedState = (state: StudyState): boolean => LAUNCHED_STATES.has(state);

/**
 * Whether the top of the sidebar just started saying something the researcher has to act on.
 *
 * The sidebar is several screens tall and its approvals are at the bottom of each card, so the
 * moment approving the last artifact makes a study ready, the banner and the price announcing it
 * are far above the fold and nothing says so. This is the question of whether that just happened —
 * asked of a transition rather than of a state, because a study that was already ready when it was
 * opened must not drag the view anywhere.
 *
 * A launched state counts once, on the way in: `analyzing → complete` is progress on a panel the
 * user is already looking at, and scrolling to it again would take the page out from under them.
 */
export const readinessCameIntoView = (previous: StudyState, next: StudyState): boolean => {
  if (previous === next) {
    return false;
  }

  if (next === 'ready') {
    return true;
  }

  return LAUNCHED_STATES.has(next) && !LAUNCHED_STATES.has(previous);
};
